import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Trash2, FileText, ExternalLink } from "lucide-react";

const STATUSES = ["New", "Under Review", "Approved", "Rejected", "Converted"];
const STATUS_COLORS = {
  New: "bg-sky-100 text-sky-700",
  "Under Review": "bg-amber-100 text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-700",
  Converted: "bg-purple-100 text-purple-700",
};

export default function AdminPartnerLeads() {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");

  const loadAll = () => api.get("/admin/partner-leads").then(({ data }) => setAllItems(data)).catch(() => {});
  const load = () => {
    const params = {};
    if (filter) params.status = filter;
    if (q) params.search = q;
    api.get("/admin/partner-leads", { params }).then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load applications"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, q]);
  useEffect(() => { loadAll(); }, []);

  const refreshAll = () => { load(); loadAll(); };

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/partner-leads/${id}`, { status }); toast.success(`Marked ${status}`); refreshAll(); }
    catch { toast.error("Failed to update status"); }
  };
  const setNotes = async (id, notes) => {
    try { await api.patch(`/admin/partner-leads/${id}`, { notes }); toast.success("Notes saved"); }
    catch { toast.error("Failed to save notes"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this partner application?")) return;
    try { await api.delete(`/admin/partner-leads/${id}`); toast.success("Deleted"); refreshAll(); }
    catch { toast.error("Failed to delete"); }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: allItems.filter((i) => i.status === s).length }), {});

  return (
    <div className="p-8" data-testid="admin-partner-leads">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-3xl">Partner With Us — Leads</h1>
          <p className="text-neutral-500 mt-1">{allItems.length} applications</p>
        </div>
      </div>

      <div className="flex gap-2 mt-6 flex-wrap items-center">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${!filter ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200"}`} data-testid="partner-filter-all">All ({allItems.length})</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${filter === s ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200"}`} data-testid={`partner-filter-${s.toLowerCase().replace(/\s/g, "-")}`}>
            {s} <span className="opacity-60">({counts[s] || 0})</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / phone"
                 className="pl-9 pr-4 py-2 rounded-full border border-neutral-200 text-sm" data-testid="partner-leads-search" />
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {items.map((l) => (
          <div key={l.id} className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid={`partner-lead-${l.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold">{l.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[l.status] || "bg-neutral-100 text-neutral-600"}`}>{l.status}</span>
                </div>
                <div className="text-sm text-neutral-600 mt-1">{l.phone}{l.email ? ` • ${l.email}` : ""}</div>
                {l.course_details && <div className="text-sm text-neutral-500 mt-2">{l.course_details}</div>}
                {l.syllabus_pdf && (
                  <a href={l.syllabus_pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-sky-600 hover:underline mt-2" data-testid={`partner-lead-pdf-${l.id}`}>
                    <FileText className="w-4 h-4" /> View uploaded file <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <div className="text-xs text-neutral-400 mt-2">{new Date(l.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`partner-lead-status-${l.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(l.id)} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`partner-lead-delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <input defaultValue={l.notes || ""} onBlur={(e) => setNotes(l.id, e.target.value)} placeholder="Add notes…"
                   className="w-full mt-3 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`partner-lead-notes-${l.id}`} />
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-16 text-neutral-500">No partner applications yet.</div>}
      </div>
    </div>
  );
}
